export type LegalBlock =
  | { type: 'h2'; id: string; text: string }
  | { type: 'p'; text: string }
  | { type: 'hr' };

export interface LegalTocItem {
  id: string;
  text: string;
}

export function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 64);
}

function isAllCapsish(line: string): boolean {
  // Avoid treating normal sentences as headings; look for a strong uppercase signal.
  const letters = line.replace(/[^A-Za-z]/g, '');
  if (letters.length < 8) return false;
  const upper = letters.replace(/[^A-Z]/g, '').length;
  return upper / letters.length > 0.85;
}

function looksLikeSectionHeading(line: string): boolean {
  const s = line.trim();
  if (!s) return false;
  if (s.length > 120) return false;

  // Common legal heading patterns.
  if (/^(section\s+)?\d+(\.\d+)*\s+.+/i.test(s)) return true;
  if (/^[ivxlcdm]+\.\s+.+/i.test(s)) return true; // Roman numerals
  if (/^[A-Z]\.\s+.+/.test(s)) return true;
  if (isAllCapsish(s)) return true;
  if (/^(definitions|privacy|cookies|refund|cancellation|acceptable use|children|coppa|disclaimer|limitation of liability|arbitration|governing law)\b/i.test(s))
    return true;

  return false;
}

function dedupeIds(items: { id: string }[]): void {
  const counts = new Map<string, number>();
  for (const item of items) {
    const n = (counts.get(item.id) ?? 0) + 1;
    counts.set(item.id, n);
    if (n > 1) item.id = `${item.id}-${n}`;
  }
}

export function parseLegalTextToBlocks(text: string): { blocks: LegalBlock[]; toc: LegalTocItem[] } {
  const lines = text
    .split('\n')
    .map((l) => l.replace(/\s+/g, ' ').trim())
    .filter((l) => l.length > 0);

  const blocks: LegalBlock[] = [];
  const toc: LegalTocItem[] = [];

  let paragraph: string[] = [];
  const flushParagraph = () => {
    const p = paragraph.join(' ').replace(/\s+/g, ' ').trim();
    if (p) blocks.push({ type: 'p', text: p });
    paragraph = [];
  };

  for (const line of lines) {
    // Page headers/footers often repeat; we can skip very short “Page X” style lines.
    if (/^page\s+\d+(\s+of\s+\d+)?$/i.test(line)) continue;

    // Horizontal breaks sometimes show up as underscores/dashes.
    if (/^[-_]{8,}$/.test(line)) {
      flushParagraph();
      blocks.push({ type: 'hr' });
      continue;
    }

    if (looksLikeSectionHeading(line)) {
      flushParagraph();
      const id = slugify(line);
      blocks.push({ type: 'h2', id, text: line });
      toc.push({ id, text: line });
      continue;
    }

    // Bullet-ish lines: keep them as separate sentences so they don’t get mashed.
    if (/^[-•]\s+/.test(line) || /^\(\w+\)\s+/.test(line) || /^\d+\)\s+/.test(line)) {
      flushParagraph();
      blocks.push({ type: 'p', text: line });
      continue;
    }

    paragraph.push(line);
  }

  flushParagraph();
  dedupeIds(blocks.filter((b): b is { id: string } => (b as any).id));
  dedupeIds(toc);

  return { blocks, toc };
}

export function extractTocFromMarkdown(md: string): LegalTocItem[] {
  const lines = md.split('\n');
  const toc: LegalTocItem[] = [];

  for (const line of lines) {
    const m = line.match(/^##\s+(.+)\s*$/);
    if (!m) continue;
    const text = m[1].trim();
    if (!text) continue;
    // Only include numbered sections (e.g. "1. Title", "2.1. Title") in the TOC.
    if (!/^\d+(\.\d+)*\.\s+/.test(text)) continue;
    toc.push({ id: slugify(text), text });
  }

  dedupeIds(toc);
  return toc;
}

